import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
  X,
  FileText,
  Headphones,
  Camera,
  Youtube,
  Lock,
  StickyNote,
  Download,
  Trash2,
  ZoomIn,
  Upload,
  ExternalLink,
  Copy,
  Save,
  Video,
  ImageIcon,
} from 'lucide-react';
import { usePrivacy } from '../../contexts/PrivacyContext';
import DarkDatePicker from '../ui/DarkDatePicker';
import {
  calculateProgress,
  getProgressColor,
  isVideoOverdue,
  calculateVideoStatus,
  COLUMNS,
} from './kanbanUtils';

/**
 * KanbanVideoModal — Full video edit modal opened by clicking a Kanban card
 *
 * Props:
 *   video: object | null       — video to edit (null = closed)
 *   onClose: fn                — close modal
 *   onChecklistChange: fn      — (videoId, field, checked) => void
 *   onVideoUpdate: fn          — (videoId, data) => Promise
 *   onOpenScript: fn           — (video) => void
 *   onThumbnailUpload: fn      — (videoId) => void — triggers file input
 *   onThumbnailDelete: fn     — (videoId) => void — delete thumbnail
 *   onDeleteVideo: fn         — (videoId) => void — delete video (with confirm)
 *   canalId: string
 */
const KanbanVideoModal = ({
  video,
  onClose,
  onChecklistChange,
  onVideoUpdate,
  onOpenScript,
  onThumbnailUpload,
  onThumbnailDelete,
  onDeleteVideo,
  onLinkYoutube,
  canalId,
}) => {
  const { isPrivate } = usePrivacy();

  // Local state for editable fields
  const [titulo, setTitulo] = useState('');
  const [dataPostagem, setDataPostagem] = useState('');
  const [notas, setNotas] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [saving, setSaving] = useState(false);
  const [thumbHover, setThumbHover] = useState(false);
  // Track initial values to detect changes
  const [initialTitulo, setInitialTitulo] = useState('');
  const [initialNotas, setInitialNotas] = useState('');
  const [initialThumbnail, setInitialThumbnail] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state only when a DIFFERENT video is opened (id changes).
  // Using video.id as dependency instead of the full video object prevents
  // resetting user edits when the parent re-derives currentModalVideo
  // (which creates a new object reference on every videos state change).
  const videoId = video?.id;
  useEffect(() => {
    if (video) {
      const t = video.titulo || '';
      const n = video.notas || '';
      const th = video.thumbnail || '';
      setTitulo(t);
      setNotas(n);
      setThumbnail(th);
      setInitialTitulo(t);
      setInitialNotas(n);
      setInitialThumbnail(th);
      setHasChanges(false);
      // Convert DD/MM/YYYY to YYYY-MM-DD for DarkDatePicker
      if (video.data_postagem) {
        const parts = video.data_postagem.split('/');
        if (parts.length === 3) {
          setDataPostagem(`${parts[2]}-${parts[1]}-${parts[0]}`);
        } else {
          setDataPostagem(video.data_postagem);
        }
      } else {
        setDataPostagem('');
      }
    }
  }, [videoId]);

  if (!video) return null;

  const progress = calculateProgress(video);
  const progressColor = getProgressColor(progress);
  const overdue = isVideoOverdue(video);
  const status = calculateVideoStatus(video);
  const statusColumn = COLUMNS.find((c) => c.id === status);
  // Vinculado ao YouTube: a thumb sincronizada VENCE a manual (cobre o card
  // que tinha thumb própria antes do vínculo). Desvinculado volta ao padrão.
  const thumbnailUrl = (video.youtube_video_id && video.youtube_thumbnail)
    ? video.youtube_thumbnail
    : (video.thumbnail_url || video.youtube_thumbnail || null);
  // Thumb travada: importado OU vinculado (upload/troca/remoção bloqueados)
  const thumbLocked = !!(video.is_imported || video.youtube_video_id);

  const checklistItems = [
    { key: 'checklist_titulo', label: 'Titulo', checked: !!(video.checklist_titulo || '').trim() },
    { key: 'checklist_roteiro', label: 'Roteiro', checked: !!(video.checklist_roteiro || '').trim() },
    { key: 'checklist_audio', label: 'Audio', checked: !!(video.checklist_audio || '').trim() },
    { key: 'checklist_thumbnail', label: 'Thumbnail', checked: !!(video.checklist_thumbnail || '').trim() },
    { key: 'checklist_pronto', label: 'Renderizado', checked: !!(video.checklist_pronto || '').trim() },
    { key: 'checklist_postagem', label: 'Publicado', checked: !!(video.checklist_postagem || '').trim() },
  ];

  // Save a single field on blur
  const handleFieldSave = async (field, value) => {
    if (!onVideoUpdate) return;
    setSaving(true);
    try {
      await onVideoUpdate(video.id, { [field]: value });
    } catch {
      // Error toast handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = (val) => {
    setDataPostagem(val);
    // Convert YYYY-MM-DD to DD/MM/YYYY for backend
    if (val) {
      const [y, m, d] = val.split('-');
      handleFieldSave('data_postagem', `${d}/${m}/${y}`);
    } else {
      handleFieldSave('data_postagem', '');
    }
  };

  const handleCheckbox = (key, currentlyChecked) => {
    if (video.is_imported) return;
    onChecklistChange(video.id, key, !currentlyChecked);
  };

  // Save all pending changes (title + notes)
  const handleSaveAll = async () => {
    if (!onVideoUpdate || !hasChanges) return;
    setSaving(true);
    try {
      const updates = {};
      if (titulo !== initialTitulo) updates.titulo = titulo;
      if (notas !== initialNotas) updates.notas = notas;
      if (thumbnail !== initialThumbnail) updates.thumbnail = thumbnail;
      if (Object.keys(updates).length > 0) {
        await onVideoUpdate(video.id, updates);
        setInitialTitulo(titulo);
        setInitialNotas(notas);
        setInitialThumbnail(thumbnail);
      }
      setHasChanges(false);
      toast.success('Alterações salvas!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const sectionTitle = {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--dp-text-tertiary, #6B7280)',
    marginBottom: '8px',
    fontFamily: 'var(--dp-font)',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--dp-bg-elevated, rgba(15,23,42,0.6))',
    border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.3))',
    borderRadius: '8px',
    color: 'var(--dp-text-primary, #F5F5F5)',
    fontSize: '13px',
    fontFamily: 'var(--dp-font)',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  return (
    <Dialog open={!!video} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="dp-hide-dialog-x"
        aria-describedby={undefined}
        style={{
          background: 'var(--dp-bg-glass, rgba(28, 28, 28, 0.92))',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium, rgba(71,85,105,0.4))',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          padding: 0,
          maxWidth: '520px',
          width: 'calc(100% - 32px)',
        }}
      >
        {/* Accessibility: hidden DialogTitle */}
        <DialogTitle style={{
          position: 'absolute', width: '1px', height: '1px',
          overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap',
        }}>
          {isPrivate ? 'Vídeo' : (video?.titulo || 'Editar Video')}
        </DialogTitle>

        {/* Header with thumbnail */}
        <div style={{ position: 'relative' }}>
          {/* Thumbnail */}
          <div
            style={{
              width: '100%',
              aspectRatio: '16/9',
              background: 'var(--dp-bg-card, rgba(30,41,59,0.8))',
              overflow: 'hidden',
              position: 'relative',
              cursor: !thumbLocked && !thumbnailUrl ? 'pointer' : 'default',
              borderRadius: '16px 16px 0 0',
            }}
            onClick={() => {
              // Only click-to-upload when there's no thumbnail (empty state)
              if (thumbLocked || thumbnailUrl) return;
              if (onThumbnailUpload) onThumbnailUpload(video.id);
            }}
            onMouseEnter={() => setThumbHover(true)}
            onMouseLeave={() => setThumbHover(false)}
          >
            {isPrivate ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(30, 41, 59, 0.8)',
                }}
              >
                <Video
                  size={40}
                  style={{ color: 'var(--dp-text-tertiary, #4B5563)' }}
                />
              </div>
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={video.titulo || 'Thumbnail'}
                referrerPolicy="no-referrer"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Camera
                  size={32}
                  style={{ color: 'var(--dp-text-tertiary, #4B5563)' }}
                />
                {!thumbLocked && (
                  <span style={{
                    fontSize: '12px',
                    color: 'var(--dp-text-tertiary, #6B7280)',
                    fontFamily: 'var(--dp-font)',
                  }}>
                    Clique para adicionar thumbnail
                  </span>
                )}
              </div>
            )}

            {/* Thumbnail action bar on hover */}
            {!isPrivate && thumbnailUrl && thumbHover && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Preview */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(thumbnailUrl, '_blank');
                  }}
                  title="Visualizar"
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                >
                  <ZoomIn size={18} style={{ color: 'white' }} />
                </button>

                {/* Replace (somente thumb editável: nem importado nem vinculado) */}
                {!thumbLocked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onThumbnailUpload) onThumbnailUpload(video.id);
                    }}
                    title="Substituir"
                    style={{
                      background: 'rgba(255,255,255,0.18)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                  >
                    <Upload size={18} style={{ color: 'white' }} />
                  </button>
                )}

                {/* Download */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(thumbnailUrl, '_blank');
                  }}
                  title="Baixar"
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                >
                  <Download size={18} style={{ color: 'white' }} />
                </button>

                {/* Remove (somente thumb editável: nem importado nem vinculado) */}
                {!thumbLocked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onThumbnailDelete) onThumbnailDelete(video.id);
                    }}
                    title="Remover"
                    style={{
                      background: 'rgba(239,68,68,0.35)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.55)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.35)'; }}
                  >
                    <Trash2 size={18} style={{ color: '#FCA5A5' }} />
                  </button>
                )}
              </div>
            )}

            {/* Gradient overlay */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '80px',
                background:
                  'linear-gradient(transparent, var(--dp-bg-glass, rgba(28,28,28,0.92)))',
                pointerEvents: 'none',
              }}
            />

            {/* YouTube badge: importado OU vinculado (thumb sincronizada) */}
            {thumbLocked && (
              <div
                title={video.is_imported ? '' : 'Thumbnail sincronizada do YouTube'}
                style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  background: 'rgba(255, 0, 0, 0.9)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'white',
                  fontFamily: 'var(--dp-font)',
                }}
              >
                <Youtube size={12} />
                <span>YouTube</span>
                <Lock size={10} style={{ marginLeft: '2px' }} />
              </div>
            )}

            {/* Status badge */}
            {statusColumn && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  background: statusColumn.bgColor,
                  border: `1px solid ${statusColumn.color}40`,
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: statusColumn.color,
                  fontFamily: 'var(--dp-font)',
                }}
              >
                <span>{statusColumn.icon}</span>
                <span>{statusColumn.title}</span>
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(8px)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              padding: 0,
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.5)';
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px 24px 24px' }}>
          {/* Title */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={sectionTitle}>Titulo</div>
              {!isPrivate && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  color: titulo.length > 100
                    ? 'var(--dp-red-500, #F87171)'
                    : 'var(--dp-blue-500, #0DAEFF)',
                }}>
                  {titulo.length}/100
                </span>
              )}
            </div>
            <input
              type="text"
              value={isPrivate ? '••••••••••' : titulo}
              onChange={(e) => {
                if (isPrivate) return;
                setTitulo(e.target.value);
                setHasChanges(e.target.value !== initialTitulo || notas !== initialNotas || thumbnail !== initialThumbnail);
              }}
              disabled={video.is_imported || isPrivate}
              placeholder={isPrivate ? '' : 'Titulo do video...'}
              style={{
                ...inputStyle,
                opacity: (video.is_imported || isPrivate) ? 0.6 : 1,
                cursor: (video.is_imported || isPrivate) ? 'not-allowed' : 'text',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.3))';
              }}
            />
          </div>

          {/* Date + Progress row */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={sectionTitle}>Data de Postagem</div>
              {video.is_imported ? (
                <input
                  type="text"
                  value={dataPostagem ? (() => {
                    const parts = dataPostagem.split('-');
                    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dataPostagem;
                  })() : ''}
                  disabled
                  style={{
                    ...inputStyle,
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  }}
                />
              ) : (
                <DarkDatePicker
                  value={dataPostagem}
                  onChange={handleDateChange}
                  placeholder="Selecionar data..."
                />
              )}
              {overdue && (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: '4px',
                    padding: '2px 6px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    color: '#EF4444',
                    fontFamily: 'var(--dp-font)',
                  }}
                >
                  Atrasado
                </span>
              )}
            </div>

            {/* Progress */}
            <div
              style={{
                minWidth: '100px',
                textAlign: 'center',
                paddingBottom: overdue ? '22px' : '0',
              }}
            >
              <div style={sectionTitle}>Progresso</div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: '6px',
                    background: 'var(--dp-border-subtle, rgba(71,85,105,0.3))',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progress}%`,
                      borderRadius: '3px',
                      background: progressColor,
                      transition: 'width 0.3s ease, background-color 0.3s ease',
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: progressColor,
                    fontFamily: 'var(--dp-font)',
                    minWidth: '32px',
                  }}
                >
                  {progress}%
                </span>
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div style={{ marginBottom: '16px' }}>
            <div style={sectionTitle}>Checklist</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
              }}
            >
              {checklistItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleCheckbox(item.key, item.checked)}
                  disabled={video.is_imported}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    background: item.checked
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'var(--dp-bg-elevated, rgba(15,23,42,0.4))',
                    border: `1px solid ${
                      item.checked
                        ? 'rgba(16, 185, 129, 0.3)'
                        : 'var(--dp-border-subtle, rgba(71,85,105,0.2))'
                    }`,
                    borderRadius: '8px',
                    cursor: video.is_imported ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--dp-font)',
                    opacity: video.is_imported ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '4px',
                      border: `2px solid ${
                        item.checked ? '#10B981' : 'var(--dp-text-tertiary, #6B7280)'
                      }`,
                      background: item.checked ? '#10B981' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {item.checked && (
                      <span style={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>
                        {'\u2713'}
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: item.checked ? 500 : 400,
                      color: item.checked ? '#10B981' : 'var(--dp-text-secondary, #A0A0A0)',
                      textDecoration: item.checked ? 'line-through' : 'none',
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Thumbnail (texto descritivo) */}
          <div style={{ marginBottom: '20px' }}>
            <div style={sectionTitle}>
              <ImageIcon
                size={11}
                style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}
              />
              Thumbnail
            </div>
            <input
              type="text"
              value={isPrivate ? '••••••••••' : thumbnail}
              onChange={(e) => {
                if (isPrivate) return;
                setThumbnail(e.target.value);
                setHasChanges(titulo !== initialTitulo || notas !== initialNotas || e.target.value !== initialThumbnail);
              }}
              disabled={video.is_imported || isPrivate}
              placeholder={isPrivate ? '' : 'Descrição da thumbnail'}
              style={{
                ...inputStyle,
                opacity: (video.is_imported || isPrivate) ? 0.6 : 1,
              }}
            />
          </div>

          {/* Descrição */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={sectionTitle}>
                <StickyNote
                  size={11}
                  style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}
                />
                Descrição
              </div>
              {!isPrivate && (notas || '').trim() && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(notas || '');
                    toast.success('Descrição copiada!');
                  }}
                  title="Copiar descrição"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    background: 'transparent',
                    border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.25))',
                    borderRadius: '6px',
                    color: 'var(--dp-text-tertiary, #9CA3AF)',
                    fontSize: '10px',
                    fontWeight: 500,
                    fontFamily: 'var(--dp-font)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                    e.currentTarget.style.color = 'var(--dp-blue-500, #0DAEFF)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.25))';
                    e.currentTarget.style.color = 'var(--dp-text-tertiary, #9CA3AF)';
                  }}
                >
                  <Copy size={10} />
                  Copiar
                </button>
              )}
            </div>
            <textarea
              value={isPrivate ? 'Conteúdo oculto' : notas}
              onChange={(e) => {
                if (isPrivate) return;
                setNotas(e.target.value);
                setHasChanges(titulo !== initialTitulo || e.target.value !== initialNotas || thumbnail !== initialThumbnail);
              }}
              disabled={video.is_imported || isPrivate}
              placeholder={isPrivate ? '' : 'Adicionar descrição do vídeo...'}
              rows={3}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: '60px',
                opacity: (video.is_imported || isPrivate) ? 0.6 : 1,
                cursor: (video.is_imported || isPrivate) ? 'not-allowed' : 'text',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
              }}
              onBlurCapture={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.3))';
              }}
            />
          </div>

          {/* Quick action buttons */}
          {/* "Ver no YouTube": importados E manuais vinculados (2026-06) */}
          {(video.is_imported || video.youtube_video_id) && (
            // YouTube link (hidden in privacy mode)
            !isPrivate && video.youtube_url && (
              <a
                href={video.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(255, 0, 0, 0.08)',
                  border: '1px solid rgba(255, 0, 0, 0.20)',
                  borderRadius: '10px',
                  color: '#EF4444',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none',
                  marginBottom: '10px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.20)';
                }}
              >
                <ExternalLink size={16} />
                Ver no YouTube
              </a>
            )
          )}
          {!video.is_imported && (
            // Normal (inclui manual vinculado): Roteiro + Audio + YouTube
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <button
                onClick={() => onOpenScript?.(video)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(13, 174, 255, 0.1)',
                  border: '1px solid rgba(13, 174, 255, 0.25)',
                  borderRadius: '10px',
                  color: 'var(--dp-blue-500, #0DAEFF)',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(13, 174, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(13, 174, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.25)';
                }}
              >
                <FileText size={16} />
                Roteiro
              </button>

              <button
                onClick={() => {
                  window.location.href = `/audio-generator?videoId=${video.id}&canalId=${video.canal_id || canalId}`;
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  borderRadius: '10px',
                  color: '#8B5CF6',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.25)';
                }}
              >
                <Headphones size={16} />
                Gerar Audio
              </button>

              <button
                onClick={() => onLinkYoutube?.(video)}
                title={video.youtube_video_id ? 'YouTube vinculado' : 'Vincular ao vídeo publicado no YouTube'}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(255, 0, 0, 0.08)',
                  border: '1px solid rgba(255, 0, 0, 0.20)',
                  borderRadius: '10px',
                  color: '#EF4444',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.20)';
                }}
              >
                <Youtube size={16} />
                {video.youtube_video_id ? 'Vinculado' : 'YouTube'}
              </button>
            </div>
          )}

          {/* Footer bar: Delete (left) | Cancel + Save (right) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '16px',
              borderTop: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.2))',
              marginTop: '6px',
            }}
          >
            {/* Delete button */}
            <button
              onClick={() => onDeleteVideo?.(video.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 14px',
                background: 'transparent',
                border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.3))',
                borderRadius: '10px',
                color: 'var(--dp-text-tertiary, #6B7280)',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: 'var(--dp-font)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
                e.currentTarget.style.color = '#EF4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.3))';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--dp-text-tertiary, #6B7280)';
              }}
            >
              <Trash2 size={14} />
              Excluir
            </button>

            {/* Right side: Cancel + Save */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onClose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '9px 16px',
                  background: 'transparent',
                  border: '1px solid var(--dp-border-subtle, rgba(71,85,105,0.3))',
                  borderRadius: '10px',
                  color: 'var(--dp-text-secondary, #9CA3AF)',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-medium, rgba(71,85,105,0.5))';
                  e.currentTarget.style.color = 'var(--dp-text-primary, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(71,85,105,0.3))';
                  e.currentTarget.style.color = 'var(--dp-text-secondary, #9CA3AF)';
                }}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveAll}
                disabled={!hasChanges || saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '9px 20px',
                  background: hasChanges && !saving
                    ? 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)'
                    : 'var(--dp-border-subtle, rgba(71,85,105,0.2))',
                  border: 'none',
                  borderRadius: '10px',
                  color: hasChanges && !saving ? '#fff' : 'var(--dp-text-tertiary, #6B7280)',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  opacity: hasChanges && !saving ? 1 : 0.6,
                }}
              >
                <Save size={14} />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>

        {/* CSS: hide Radix default close button */}
        <style>{`
          .dp-hide-dialog-x > button.absolute { display: none !important; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default KanbanVideoModal;
